import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import campaignService from '../services/campaignService';
import onboardingService from '../services/onboardingService';
import { OnboardingBlueprint } from './Onboarding/Types';
import BlueprintPreviewPanel from './BlueprintPreviewPanel';
import AICopyEditorTool from './AICopyEditorTool';

export interface ToolsPanelProps {
  newsletterEdition: any;
  onClose: () => void;
  onUpdateTitle?: (newTitle: string) => void;
  onTemplateUpdated?: () => void;
}

const ToolsPanel: React.FC<ToolsPanelProps> = ({ newsletterEdition, onClose, onUpdateTitle, onTemplateUpdated }) => {
  const navigate = useNavigate();
  const [subjectLines, setSubjectLines] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [additionalInstructions, setAdditionalInstructions] = useState<string>('');
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [backupStatus, setBackupStatus] = useState<string | null>(null);
  const [introInstructions, setIntroInstructions] = useState<string>('');
  const [isGeneratingIntro, setIsGeneratingIntro] = useState(false);
  const [generatedIntro, setGeneratedIntro] = useState<string>('');
  const [isCopying, setIsCopying] = useState(false);
  const [isEvaluatingContent, setIsEvaluatingContent] = useState(false);
  const [evaluationResults, setEvaluationResults] = useState<any>(null);
  const [showBlueprintPreview, setShowBlueprintPreview] = useState(false);
  const [blueprintData, setBlueprintData] = useState<OnboardingBlueprint | null>(null);
  const [isLoadingBlueprint, setIsLoadingBlueprint] = useState(false);


  const generateNewsletterIntro = async () => {
    setIsGeneratingIntro(true);
    try {
      const response = await campaignService.generateNewsletterIntro(
        newsletterEdition.id,
        introInstructions.trim() || undefined
      );
      if (response && response.intro) {
        setGeneratedIntro(response.intro);
      }
    } catch (error) {
      console.error('Error generating newsletter intro:', error);
      // Fallback intro on error
      setGeneratedIntro('Welcome to this week\'s newsletter! We\'ve curated the most important insights and updates just for you.');
    } finally {
      setIsGeneratingIntro(false);
    }
  };

  const copyIntroToClipboard = async () => {
    if (generatedIntro) {
      try {
        await navigator.clipboard.writeText(generatedIntro);
        // Could add a temporary success message here
      } catch (err) {
        console.error('Failed to copy intro to clipboard:', err);
        // Fallback for browsers that don't support clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = generatedIntro;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    }
  };

  const handleBackupTemplate = async () => {
    setIsBackingUp(true);
    setBackupStatus(null);
    try {
      const response = await campaignService.backupEmailTemplate(newsletterEdition.id);
      setBackupStatus('✅ Backup saved successfully!');
      setTimeout(() => setBackupStatus(null), 3000);
    } catch (error) {
      console.error('Error backing up template:', error);
      setBackupStatus('❌ Failed to save backup');
      setTimeout(() => setBackupStatus(null), 5000);
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleRestoreTemplate = async () => {
    if (!window.confirm('Are you sure you want to restore from backup? This will replace your current email template.')) {
      return;
    }

    // Reload the page immediately after confirmation
    setIsRestoring(true);
    setBackupStatus(null);
    try {
      const response = await campaignService.restoreEmailTemplate(newsletterEdition.id);
      setBackupStatus('✅ Template restored from backup! Reloading...');
      // Reload immediately to show the restored template
      window.location.reload();
    } catch (error: any) {
      console.error('Error restoring template:', error);
      const errorMessage = error.response?.data?.error || 'Failed to restore from backup';
      setBackupStatus(`❌ ${errorMessage}`);
      setTimeout(() => setBackupStatus(null), 5000);
      setIsRestoring(false);
    }
  };

  const handleCopyToNew = async () => {
    setIsCopying(true);
    try {
      const response = await campaignService.copyNewsletterEdition(newsletterEdition.id);
      alert('Newsletter copied successfully!');
      setIsCopying(false);
    } catch (error: any) {
      console.error('Error copying newsletter edition:', error);
      alert('Failed to copy newsletter edition. Please try again.');
      setIsCopying(false);
    }
  };

  const generateSubjectLines = async () => {
    setIsGenerating(true);
    setSelectedSubject(null);
    try {
      const response = await campaignService.generateSubjectLines(
        newsletterEdition.id,
        additionalInstructions.trim() || undefined
      );
      if (response && response.subject_lines) {
        setSubjectLines(response.subject_lines);
      }
    } catch (error) {
      console.error('Error generating subject lines:', error);
      // Fallback subjects on error
      setSubjectLines([
        `${newsletterEdition.title} - Latest Edition`,
        `Your Weekly ${newsletterEdition.title}`,
        `New: ${newsletterEdition.title} Updates`
      ]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectSubject = (subject: string) => {
    setSelectedSubject(subject);
    if (onUpdateTitle) {
      onUpdateTitle(subject);
    }
  };

  const handleEvaluateContent = async () => {
    setIsEvaluatingContent(true);
    setEvaluationResults(null);
    try {
      const response = await campaignService.evaluateContent(newsletterEdition.id);
      setEvaluationResults(response);
    } catch (error) {
      console.error('Error evaluating content:', error);
      setEvaluationResults({
        pattern_issues: [],
        ai_issues: [],
        error: 'Failed to analyze content. Please try again.'
      });
    } finally {
      setIsEvaluatingContent(false);
    }
  };

  const handlePreviewBlueprint = async () => {
    if (!newsletterEdition.onboarding_blueprint_id) return;
    setIsLoadingBlueprint(true);
    try {
      const blueprint = await onboardingService.getOnboardingBlueprintById(
        newsletterEdition.onboarding_blueprint_id
      );
      setBlueprintData(blueprint);
      setShowBlueprintPreview(true);
    } catch (error) {
      console.error('Error fetching blueprint:', error);
    } finally {
      setIsLoadingBlueprint(false);
    }
  };

  // Show blueprint preview panel if active
  if (showBlueprintPreview && blueprintData) {
    return (
      <BlueprintPreviewPanel
        blueprint={blueprintData}
        onBack={() => setShowBlueprintPreview(false)}
      />
    );
  }

  return (
    <div style={{
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    }}>
      <h2 style={{
        fontSize: '1.5em',
        fontWeight: 'bold',
        marginBottom: '20px',
        borderBottom: '1px solid #e0e0e0',
        paddingBottom: '10px',
      }}>
        Tools
      </h2>

      {/* Blueprint Section */}
      {newsletterEdition.onboarding_blueprint_id && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
          <h3 style={{
            fontSize: '1.1em',
            fontWeight: '600',
            color: '#333',
          }}>
            Blueprint
          </h3>
          <Link
            to={`/blueprints/${newsletterEdition.onboarding_blueprint_id}`}
            onClick={onClose}
            style={{
              display: 'block',
              padding: '10px 16px',
              backgroundColor: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '6px',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: '0.95em',
              transition: 'background-color 0.2s',
              textDecoration: 'none',
              color: 'inherit',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e0e0e0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
            }}
          >
            📋 Navigate to Blueprint
          </Link>
          <button
            onClick={handlePreviewBlueprint}
            disabled={isLoadingBlueprint}
            style={{
              display: 'block',
              width: '100%',
              padding: '10px 16px',
              backgroundColor: isLoadingBlueprint ? '#f8f8f8' : '#f0f0f0',
              border: `1px solid ${isLoadingBlueprint ? '#ddd' : '#ccc'}`,
              borderRadius: '6px',
              cursor: isLoadingBlueprint ? 'not-allowed' : 'pointer',
              textAlign: 'left',
              fontSize: '0.95em',
              transition: 'background-color 0.2s',
              color: isLoadingBlueprint ? '#999' : 'inherit',
            }}
            onMouseEnter={(e) => {
              if (!isLoadingBlueprint) {
                e.currentTarget.style.backgroundColor = '#e0e0e0';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoadingBlueprint) {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }
            }}
          >
            {isLoadingBlueprint ? '⏳ Loading...' : '👁️ Preview Blueprint Sections'}
          </button>
          <Link
            to={`/blueprints/${newsletterEdition.onboarding_blueprint_id}?tab=template`}
            onClick={onClose}
            style={{
              display: 'block',
              padding: '10px 16px',
              backgroundColor: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '6px',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: '0.95em',
              transition: 'background-color 0.2s',
              textDecoration: 'none',
              color: 'inherit',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e0e0e0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
            }}
          >
            🎨 Edit Blueprint Template
          </Link>
        </div>
      )}

      {/* Subject Line Generator Section */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}>
        <h3 style={{
          fontSize: '1.1em',
          fontWeight: '600',
          color: '#333',
        }}>
          Subject Line Generator
        </h3>
        <textarea
          value={additionalInstructions}
          onChange={(e) => setAdditionalInstructions(e.target.value)}
          placeholder="Optional: Add specific instructions (e.g., 'Make it sound urgent', 'Include numbers', 'Focus on benefits')"
          style={{
            width: '100%',
            minHeight: '60px',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontFamily: 'inherit',
            resize: 'vertical',
            marginBottom: '8px',
          }}
        />
        <button
          onClick={generateSubjectLines}
          disabled={isGenerating}
          style={{
            padding: '10px 16px',
            backgroundColor: isGenerating ? '#f8f8f8' : '#4CAF50',
            border: `1px solid ${isGenerating ? '#ddd' : '#45a049'}`,
            borderRadius: '6px',
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            textAlign: 'left',
            fontSize: '0.95em',
            color: isGenerating ? '#999' : 'white',
            fontWeight: '500',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            if (!isGenerating) {
              e.currentTarget.style.backgroundColor = '#45a049';
            }
          }}
          onMouseLeave={(e) => {
            if (!isGenerating) {
              e.currentTarget.style.backgroundColor = '#4CAF50';
            }
          }}
        >
          {isGenerating ? '⏳ Generating...' : '✨ Generate 3 Subject Lines'}
        </button>

        {/* Subject Line Options */}
        {subjectLines.length > 0 && (
          <div style={{
            marginTop: '8px',
            padding: '12px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
          }}>
            <p style={{
              fontSize: '0.9em',
              color: '#666',
              marginBottom: '12px',
              fontStyle: 'italic',
            }}>
              Click a subject line to apply it to your newsletter:
            </p>
            {subjectLines.map((subject, index) => (
              <button
                key={index}
                onClick={() => handleSelectSubject(subject)}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: '8px',
                  backgroundColor: selectedSubject === subject ? '#e8f5e9' : 'white',
                  border: `2px solid ${selectedSubject === subject ? '#4CAF50' : '#ddd'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '0.9em',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (selectedSubject !== subject) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#999';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedSubject !== subject) {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#ddd';
                  }
                }}
              >
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <span style={{
                    flex: 1,
                    marginRight: '8px',
                  }}>
                    {subject}
                  </span>
                  {selectedSubject === subject && (
                    <span style={{
                      color: '#4CAF50',
                      fontSize: '1.2em',
                    }}>
                      ✓
                    </span>
                  )}
                </span>
              </button>
            ))}
            {selectedSubject && (
              <p style={{
                marginTop: '12px',
                fontSize: '0.85em',
                color: '#4CAF50',
                fontWeight: '500',
                textAlign: 'center',
              }}>
                ✅ Subject line applied!
              </p>
            )}
          </div>
        )}
      </div>

      {/* Newsletter Intro Generator Section */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}>
        <h3 style={{
          fontSize: '1.1em',
          fontWeight: '600',
          color: '#333',
        }}>
          Newsletter Intro Generator
        </h3>
        <textarea
          value={introInstructions}
          onChange={(e) => setIntroInstructions(e.target.value)}
          placeholder="Optional: Add specific instructions (e.g., 'Keep it casual', 'Mention seasonal context', 'Focus on value proposition')"
          style={{
            width: '100%',
            minHeight: '60px',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontFamily: 'inherit',
            resize: 'vertical',
            marginBottom: '8px',
          }}
        />
        <button
          onClick={generateNewsletterIntro}
          disabled={isGeneratingIntro}
          style={{
            padding: '10px 16px',
            backgroundColor: isGeneratingIntro ? '#f8f8f8' : '#9C27B0',
            border: `1px solid ${isGeneratingIntro ? '#ddd' : '#7B1FA2'}`,
            borderRadius: '6px',
            cursor: isGeneratingIntro ? 'not-allowed' : 'pointer',
            textAlign: 'left',
            fontSize: '0.95em',
            color: isGeneratingIntro ? '#999' : 'white',
            fontWeight: '500',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            if (!isGeneratingIntro) {
              e.currentTarget.style.backgroundColor = '#7B1FA2';
            }
          }}
          onMouseLeave={(e) => {
            if (!isGeneratingIntro) {
              e.currentTarget.style.backgroundColor = '#9C27B0';
            }
          }}
        >
          {isGeneratingIntro ? '⏳ Generating...' : '📝 Generate Intro'}
        </button>

        {/* Generated Intro Display */}
        {generatedIntro && (
          <div style={{
            marginTop: '8px',
            padding: '12px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
          }}>
            <p style={{
              fontSize: '0.9em',
              color: '#666',
              marginBottom: '8px',
              fontStyle: 'italic',
            }}>
              Generated intro:
            </p>
            <div style={{
              padding: '12px',
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '0.95em',
              lineHeight: '1.5',
              color: '#333',
              marginBottom: '12px',
            }}>
              {generatedIntro}
            </div>
            <button
              onClick={copyIntroToClipboard}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#4CAF50',
                border: '1px solid #45a049',
                borderRadius: '6px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.9em',
                fontWeight: '500',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#45a049';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#4CAF50';
              }}
            >
              📋 Copy to Clipboard
            </button>
          </div>
        )}
      </div>

      {/* AI Copy Editor Section */}
      {onTemplateUpdated && (
        <AICopyEditorTool
          newsletterEdition={newsletterEdition}
          onTemplateUpdated={onTemplateUpdated}
        />
      )}

      {/* Template Backup Section */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}>
        <h3 style={{
          fontSize: '1.1em',
          fontWeight: '600',
          color: '#333',
        }}>
          Template Backup
        </h3>
        <div style={{
          display: 'flex',
          gap: '8px',
        }}>
          <button
            onClick={handleBackupTemplate}
            disabled={isBackingUp || isRestoring}
            style={{
              flex: 1,
              padding: '10px 16px',
              backgroundColor: isBackingUp ? '#f8f8f8' : '#2196F3',
              border: `1px solid ${isBackingUp ? '#ddd' : '#1976D2'}`,
              borderRadius: '6px',
              cursor: isBackingUp || isRestoring ? 'not-allowed' : 'pointer',
              textAlign: 'center',
              fontSize: '0.95em',
              color: isBackingUp ? '#999' : 'white',
              fontWeight: '500',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!isBackingUp && !isRestoring) {
                e.currentTarget.style.backgroundColor = '#1976D2';
              }
            }}
            onMouseLeave={(e) => {
              if (!isBackingUp && !isRestoring) {
                e.currentTarget.style.backgroundColor = '#2196F3';
              }
            }}
          >
            {isBackingUp ? '⏳ Saving...' : '💾 Save Backup'}
          </button>
          <button
            onClick={handleRestoreTemplate}
            disabled={isRestoring || isBackingUp}
            style={{
              flex: 1,
              padding: '10px 16px',
              backgroundColor: isRestoring ? '#f8f8f8' : '#FF9800',
              border: `1px solid ${isRestoring ? '#ddd' : '#F57C00'}`,
              borderRadius: '6px',
              cursor: isRestoring || isBackingUp ? 'not-allowed' : 'pointer',
              textAlign: 'center',
              fontSize: '0.95em',
              color: isRestoring ? '#999' : 'white',
              fontWeight: '500',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!isRestoring && !isBackingUp) {
                e.currentTarget.style.backgroundColor = '#F57C00';
              }
            }}
            onMouseLeave={(e) => {
              if (!isRestoring && !isBackingUp) {
                e.currentTarget.style.backgroundColor = '#FF9800';
              }
            }}
          >
            {isRestoring ? '⏳ Restoring...' : '♻️ Restore Backup'}
          </button>
        </div>
        {backupStatus && (
          <p style={{
            marginTop: '8px',
            fontSize: '0.9em',
            color: backupStatus.startsWith('✅') ? '#4CAF50' : '#f44336',
            fontWeight: '500',
            textAlign: 'center',
            padding: '8px',
            backgroundColor: backupStatus.startsWith('✅') ? '#e8f5e9' : '#ffebee',
            borderRadius: '4px',
          }}>
            {backupStatus}
          </p>
        )}
      </div>

      {/* Copy to New Section */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}>
        <h3 style={{
          fontSize: '1.1em',
          fontWeight: '600',
          color: '#333',
        }}>
          Newsletter Actions
        </h3>
        <button
          onClick={handleEvaluateContent}
          disabled={isEvaluatingContent}
          style={{
            padding: '10px 16px',
            backgroundColor: isEvaluatingContent ? '#f8f8f8' : '#E91E63',
            border: `1px solid ${isEvaluatingContent ? '#ddd' : '#C2185B'}`,
            borderRadius: '6px',
            cursor: isEvaluatingContent ? 'not-allowed' : 'pointer',
            textAlign: 'center',
            fontSize: '0.95em',
            color: isEvaluatingContent ? '#999' : 'white',
            fontWeight: '500',
            transition: 'all 0.2s',
            marginBottom: '8px',
          }}
          onMouseEnter={(e) => {
            if (!isEvaluatingContent) {
              e.currentTarget.style.backgroundColor = '#C2185B';
            }
          }}
          onMouseLeave={(e) => {
            if (!isEvaluatingContent) {
              e.currentTarget.style.backgroundColor = '#E91E63';
            }
          }}
        >
          {isEvaluatingContent ? '⏳ Evaluating...' : '🔍 Evaluate Content'}
        </button>

        {/* Content Evaluation Results */}
        {evaluationResults && (
          <div style={{
            marginTop: '8px',
            marginBottom: '8px',
            padding: '12px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
          }}>
            {evaluationResults.error ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                color: '#721c24',
              }}>
                <span style={{ fontSize: '1.1em', marginRight: '8px' }}>❌</span>
                <span style={{ fontWeight: '600' }}>Error</span>
              </div>
            ) : (
              <>
                {/* Overall Analysis Summary */}
                {evaluationResults.pattern_summary && (
                  <div style={{
                    fontSize: '0.9em',
                    color: '#6c757d',
                    marginBottom: '16px',
                    textAlign: 'center',
                  }}>
                    {evaluationResults.pattern_summary}
                  </div>
                )}

                {/* Pattern Detection Results */}
                {evaluationResults.pattern_issues && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}>
                      <span style={{ fontSize: '1.1em', marginRight: '8px' }}>
                        {evaluationResults.pattern_issues.length > 0 ? '🛑' : '✅'}
                      </span>
                      <span style={{
                        fontWeight: '600',
                        color: '#333',
                      }}>
                        Pattern Detection: {evaluationResults.pattern_issues.length > 0
                          ? `${evaluationResults.pattern_issues.length} Issue${evaluationResults.pattern_issues.length > 1 ? 's' : ''} Found`
                          : 'No Issues Detected'
                        }
                      </span>
                    </div>

                    {evaluationResults.pattern_issues.length > 0 && (
                      <div style={{
                        fontSize: '0.9em',
                        color: '#666',
                      }}>
                        {evaluationResults.pattern_issues.map((issue: any, index: number) => (
                          <div key={index} style={{
                            marginBottom: '6px',
                            padding: '6px 8px',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                          }}>
                            <strong>{issue.type}:</strong> {issue.message}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* AI Analysis Results */}
                {evaluationResults.ai_issues && (
                  <div style={{
                    borderTop: '1px solid #e0e0e0',
                    paddingTop: '12px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}>
                      <span style={{ fontSize: '1.1em', marginRight: '8px' }}>
                        {evaluationResults.ai_issues.length > 0 ? '🤖' : '🤖'}
                      </span>
                      <span style={{
                        fontWeight: '600',
                        color: '#333',
                      }}>
                        AI Analysis: {evaluationResults.ai_issues.length > 0
                          ? `${evaluationResults.ai_issues.length} Issue${evaluationResults.ai_issues.length > 1 ? 's' : ''} Found`
                          : 'No Issues Detected'
                        }
                      </span>
                      <span style={{
                        fontSize: '0.8em',
                        color: '#999',
                        marginLeft: '8px',
                        fontStyle: 'italic',
                      }}>
                        (experimental)
                      </span>
                    </div>

                    {evaluationResults.ai_issues.length > 0 && (
                      <div style={{
                        fontSize: '0.9em',
                        color: '#666',
                      }}>
                        {evaluationResults.ai_issues.map((issue: any, index: number) => (
                          <div key={index} style={{
                            marginBottom: '6px',
                            padding: '6px 8px',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                          }}>
                            <strong>{issue.type}:</strong> {issue.message}
                          </div>
                        ))}
                      </div>
                    )}

                    {evaluationResults.ai_summary && (
                      <div style={{
                        marginTop: '8px',
                        fontSize: '0.85em',
                        color: '#6c757d',
                      }}>
                        {evaluationResults.ai_summary}
                      </div>
                    )}

                  </div>
                )}
              </>
            )}
          </div>
        )}

        <button
          onClick={handleCopyToNew}
          disabled={isCopying}
          style={{
            padding: '10px 16px',
            backgroundColor: isCopying ? '#f8f8f8' : '#673AB7',
            border: `1px solid ${isCopying ? '#ddd' : '#5E35B1'}`,
            borderRadius: '6px',
            cursor: isCopying ? 'not-allowed' : 'pointer',
            textAlign: 'center',
            fontSize: '0.95em',
            color: isCopying ? '#999' : 'white',
            fontWeight: '500',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            if (!isCopying) {
              e.currentTarget.style.backgroundColor = '#5E35B1';
            }
          }}
          onMouseLeave={(e) => {
            if (!isCopying) {
              e.currentTarget.style.backgroundColor = '#673AB7';
            }
          }}
        >
          {isCopying ? '⏳ Copying...' : '📋 Copy to New'}
        </button>
      </div>

      {/* AI Assistant Section */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}>
        <h3 style={{
          fontSize: '1.1em',
          fontWeight: '600',
          color: '#333',
        }}>
          AI Assistant
        </h3>
        <button
          disabled
          style={{
            padding: '10px 16px',
            backgroundColor: '#f8f8f8',
            border: '1px solid #ddd',
            borderRadius: '6px',
            cursor: 'not-allowed',
            textAlign: 'left',
            fontSize: '0.95em',
            color: '#999',
          }}
        >
          💬 Chat (Coming Soon)
        </button>
        <button
          disabled
          style={{
            padding: '10px 16px',
            backgroundColor: '#f8f8f8',
            border: '1px solid #ddd',
            borderRadius: '6px',
            cursor: 'not-allowed',
            textAlign: 'left',
            fontSize: '0.95em',
            color: '#999',
          }}
        >
          ✨ Smart subject and preview text (Coming Soon)
        </button>
      </div>
    </div>
  );
};

export default ToolsPanel;
